import { CheckIcon, PlusIcon } from "lucide-react";

import { OwnerAvatars } from "@/components/OwnerAvatars";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Avatar } from "@/components/quick-access/Avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell } from "@/components/ui/table";

import {
  useTaggableUsers,
  useUserOptionsWithSelected,
} from "@/contexts/TaggableUsersContext";
import { useUpdateSetJobStaffers } from "@/hooks/updateSets/useUpdateSetJobStaffers";
import { cn } from "@/lib/utils";

const MAX_VISIBLE_AVATARS = 3;

export const StafferCell = ({
  isEditable,
  jobId,
  ownerUserIds,
  align = "left",
  className,
}: {
  isEditable: boolean;
  jobId: string;
  ownerUserIds: number[];
  align?: "left" | "center";
  className?: string;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateStaffers } = useUpdateSetJobStaffers();

  /* React Hooks */
  const { userInitialsMap, userNameMap } = useTaggableUsers();
  const userOptions = useUserOptionsWithSelected(ownerUserIds);

  /* Handlers */
  const handleToggle = (userId: number) => {
    updateStaffers({
      jobId,
      userId,
      isSelected: !ownerUserIds.includes(userId),
    });
  };

  /* Render */
  const renderAvatars = () => {
    if (ownerUserIds.length === 0) {
      return (
        <Row className="h-6 w-6 items-center justify-center rounded-full border border-dashed border-lever-border-light bg-lever-surface-default-on-elevation/60 text-lever-text-label">
          <PlusIcon className="h-3.5 w-3.5" />
        </Row>
      );
    }

    return (
      <OwnerAvatars
        ownerUserIds={ownerUserIds.slice(0, MAX_VISIBLE_AVATARS)}
        userInitialsMap={userInitialsMap}
      />
    );
  };

  // Everyone sees this; only admins get a live trigger. disabled:opacity-100
  // keeps the avatars at full strength instead of dimming them.
  const renderTrigger = () => (
    <Button
      type="button"
      variant="ghost"
      disabled={!isEditable}
      className={cn(
        "absolute inset-0 h-full w-full justify-start rounded-none px-3 hover:bg-lever-surface-neutral disabled:opacity-100",
        align === "center" && "justify-center",
      )}
    >
      {renderAvatars()}
      {ownerUserIds.length > MAX_VISIBLE_AVATARS ? (
        <Text className="ml-0.5 text-[13px] font-normal tabular-nums text-lever-text-body">
          +{(ownerUserIds.length - MAX_VISIBLE_AVATARS).toString()}
        </Text>
      ) : null}
    </Button>
  );

  const renderOption = (userId: number, label: string) => {
    const isSelected = ownerUserIds.includes(userId);

    return (
      <DropdownMenuItem
        key={userId}
        className={cn(
          "justify-between gap-3",
          isSelected && "bg-lever-surface-default-on-elevation",
        )}
        onSelect={(event) => {
          // Keep the menu open so several staffers can be toggled in one go.
          event.preventDefault();
          handleToggle(userId);
        }}
      >
        <Row className="min-w-0 items-center gap-2">
          <Avatar initials={userInitialsMap[userId] ?? "?"} />
          <Text className="truncate text-sm font-normal text-lever-text-heading">
            {userNameMap[userId] ?? label}
          </Text>
        </Row>
        <CheckIcon
          className={cn("h-4 w-4 shrink-0", !isSelected && "invisible")}
        />
      </DropdownMenuItem>
    );
  };

  const renderDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{renderTrigger()}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-80 min-w-56 overflow-y-auto"
      >
        {userOptions.length === 0 ? (
          <DropdownMenuItem disabled>No staffers</DropdownMenuItem>
        ) : (
          userOptions.map((option) => renderOption(option.value, option.label))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const render = () => (
    <TableCell
      className={cn("relative h-full", className)}
      onClick={(event) => {
        // Admins click into the multiselect; for everyone else the click
        // belongs to the row's navigate-to-pipeline handler.
        if (isEditable) event.stopPropagation();
      }}
    >
      {isEditable ? renderDropdown() : renderTrigger()}
    </TableCell>
  );

  return render();
};
